<template>
  <template v-if="$route.name === 'teacher-attendances.index'">
    <div class="container-fluid px-0 " v-can="'teacher_attendance.display'">
      <TeacherAttendanceFilters @filter="handleFilter" />
      
      <div class="col-12 col-xl-12">
        <div class="card mb-3 mb-xxl-5 me-xxl-4" style="margin-top: 4rem; overflow: visible">
          <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
            <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
              <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
                {{ $t('teacher_attendance_records') }}
              </h6>
            </div>
          </div>

          <div class="card-body px-0 pb-0 pb-2 pe-xl-3">
            <div class="table-responsive p-1 p-xl-4">
              <LoadingSpinner v-if="isLoading && attendances.length === 0" 
                :message="$t('loading_data')" 
                class="py-5" 
              />

              <template v-else>
                <!-- Mobile view -->
                <div class="d-block d-xl-none">
                  <div v-if="!isLoading && attendances.length === 0" class="text-center py-3 text-muted">
                    <i class="fas fa-users-slash fa-2x mb-2"></i>
                    <p>{{ $t('no_records') }}</p>
                  </div>

                  <div v-for="attendance in attendances" :key="'mobile-' + attendance.id" class="card mb-3 mx-2">
                    <div class="card-body py-2">
                      <div class="d-flex align-items-center">
                        <div class="flex-grow-1">
<h6 class="mb-0 text-md">
  {{ attendance.teacher?.name || '-' }}
</h6>                          <p class="text-xs text-muted mb-0">
                            {{ formatDate(attendance.date) }} - {{ getDayName(attendance.day) }}
                          </p>
                        </div>
                        <div>
                          <span class="badge badge-sm" :class="getStatusBadgeClass(attendance.status)">
                            {{ getStatusName(attendance.status) }}
                          </span>
                        </div>
                      </div>
                      
                      <div class="mt-2">
                        <p class="text-xs mb-1" v-if="attendance.notes">
                          {{ attendance.notes }}
                        </p>
                        <p class="text-xs text-muted mb-1" v-else>
                          {{ $t('no_notes') }}
                        </p>
                      </div>
                      
                      <div class="d-flex justify-content-end align-items-center mt-2">
                        <div class="d-inline-flex">
                          <router-link
                            :to="`/teacher-attendances/edit/${attendance.id}`"
                            class="btn btn-link text-secondary p-0 mx-1"
                            :title="$t('edit')"
                            v-can="'teacher_attendance.update'"
                          >
                            <i class="fas fa-edit fa-xs"></i>
                          </router-link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Desktop view -->
                <table class="table align-items-center mb-0 d-none d-xl-table">
                  <thead>
                    <tr>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('teacher_name') }}</th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('date') }}</th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('attendance.day') }}</th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('attendance.status') }}</th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('notes') }}</th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center pe-4">{{ $t('attendance.actions') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!isLoading && attendances.length === 0">
                      <td colspan="6" class="text-center py-4 text-muted">
                        <i class="fas fa-users-slash fa-2x mb-2"></i>
                        <p>{{ $t('no_records') }}</p>
                      </td>
                    </tr>
                    
                    <template v-if="!isLoading">
                      <tr v-for="attendance in attendances" :key="attendance.id">
                        <td>
                          <div class="d-flex px-3 py-1">
                            <div class="d-flex flex-column justify-content-center">
<h6 class="mb-0 text-md">
  {{ attendance.teacher?.name || '-' }}
</h6>                            </div>
                          </div>
                        </td>
                        <td>
                          <p class="text-xs font-weight-bold mb-0">{{ formatDate(attendance.date) }}</p>
                        </td>
                        <td>
                          <p class="text-xs font-weight-bold mb-0">{{ getDayName(attendance.day) }}</p>
                        </td>
                        <td class="align-middle text-center">
                          <span class="badge badge-sm" :class="getStatusBadgeClass(attendance.status)">
                            {{ getStatusName(attendance.status) }}
                          </span>
                        </td>
                        <td>
                          <p class="text-xs font-weight-bold mb-0" v-if="attendance.notes">
                            {{ attendance.notes }}
                          </p>
                          <p class="text-xs text-muted mb-0" v-else>
                            {{ $t('no_notes') }}
                          </p>
                        </td>
                        <td class="align-middle text-end pe-4">
                          <div class="d-inline-flex">
                            <router-link
                              :to="`/teacher-attendances/edit/${attendance.id}`"
                              class="btn btn-link text-secondary p-1 mx-1"
                              :title="$t('edit')"
                              v-can="'teacher_attendance.update'"
                            >
                              <i class="fas fa-edit fa-sm"></i>
                            </router-link>
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
                
                <div class="d-flex justify-content-center mt-4">
                  <Pagination
                    :pagination="pagination"
                    @page-changed="onPageChanged"
                  />
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  <router-view></router-view>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import Pagination from "@/components/common/PaginationControls.vue";
import TeacherAttendanceFilters from "@/components/features/teachers/filters/TeacherAttendanceFilters.vue";
import api from "@/api";

const toast = useToast();
const { t } = useI18n(); // for translations

const attendances = ref([]);
const isLoading = ref(false);
const filters = ref({
  date: '',
  status: '',
  search: ''
});

const pagination = ref({
  currentPage: 1,
  perPage: 15,
  totalItems: 0,
  totalPages: 0,
  from: 0,
  to: 0
});

// Format date according to current locale
const formatDate = (dateString) => {
  try {
    return new Date(dateString).toLocaleDateString();
  } catch {
    return t('invalid_date');
  }
};

// Return translated day name
const getDayName = (day) => {
  const days = {
    'sunday': t('days.sunday'),
    'monday': t('days.monday'),
    'tuesday': t('days.tuesday'),
    'wednesday': t('days.wednesday'),
    'thursday': t('days.thursday'),
    'friday': t('days.friday'),
    'saturday': t('days.saturday')
  };
  return days[day] || day;
};

// Return translated status name
const getStatusName = (status) => {
  const statuses = {
    'present': t('status.present'),
    'absent': t('status.absent'),
    'late': t('status.late'),
    'excused': t('status.excused'),
    'on_leave': t('status.on_leave')
  };
  return statuses[status] || status;
};

// Badge classes remain the same
const getStatusBadgeClass = (status) => {
  const classes = {
    'present': 'bg-success',
    'absent': 'bg-danger',
    'late': 'bg-warning',
    'excused': 'bg-info',
    'on_leave': 'bg-secondary'
  };
  return classes[status] || 'bg-primary';
};

// Fetch attendances
const fetchAttendances = async () => {
  isLoading.value = true;
  try {
    const params = {
      page: pagination.value.currentPage,
      per_page: pagination.value.perPage,
      ...filters.value
    };
    
    Object.keys(params).forEach(key => {
      if (params[key] === '' || params[key] === null) {
        delete params[key];
      }
    });
    
    const response = await api.get('/teacher-attendances', { params });
    attendances.value = response.data.data;
    
    if (response.data.meta) {
      pagination.value = {
        currentPage: response.data.meta.current_page,
        perPage: response.data.meta.per_page,
        totalItems: response.data.meta.total,
        totalPages: response.data.meta.last_page,
        from: response.data.meta.from,
        to: response.data.meta.to
      };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || t('failed_loading_data');
    toast.error(errorMsg);
  } finally {
    isLoading.value = false;
  }
};

// Apply filters
const handleFilter = (filterValues) => {
  filters.value = filterValues;
  pagination.value.currentPage = 1;
  fetchAttendances();
};

// Pagination change
const onPageChanged = (page) => {
  pagination.value.currentPage = page;
  fetchAttendances();
};

onMounted(() => {
  fetchAttendances();
});
</script>
